import { companySetupApi } from "@/apis/custom/companySetup";
import { ComboBoxInput } from "@/components/shared/form/combo-box-input";
import { AddressList } from "@/components/ui/address-list";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TranslationKey } from "@/localisation/types";
import { LegalForm } from "@/queries/companies/fetch";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormValues } from "./form-schema";

interface BasicDataProps {
  form: UseFormReturn<FormValues>;
}

export function BasicData({ form }: BasicDataProps) {
  const { t } = useTranslation();

  return (
    <div className="max-w-[600px] mx-auto">
      <h2 className="text-2xl font-semibold mb-6">
        {t("mandanten_create.step1.title")}
      </h2>

      {/* Basic Information Section */}
      <div className="space-y-4 mb-8">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("mandanten_create.step1.company_name")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    "mandanten_create.step1.company_name_placeholder",
                  )}
                  onBlur={async (e) => {
                    field.onBlur();
                    const value = e.target.value?.trim();
                    if (!value) return;
                    try {
                      const res = await companySetupApi.checkCompanySlug({
                        name: value,
                      });
                      if (res?.exists) {
                        form.setError("companyName", {
                          type: "validate",
                          message: t(
                            "mandanten_create.step1.company_name_exists",
                          ),
                        });
                      } else {
                        form.clearErrors("companyName");
                      }
                    } catch (err) {}
                  }}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="vatId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("mandanten_create.step1.vat_id")}</FormLabel>
              <FormDescription className="text-sm text-muted-foreground">
                {t("mandanten_create.step1.vat_id_description")}
              </FormDescription>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("mandanten_create.step1.vat_id_placeholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Legal Form */}
        <FormField
          control={form.control}
          name="legalForm"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>{t("mandanten_create.step1.legal_form")}</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "mandanten_create.step1.legal_form_placeholder",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="z-[9999]">
                    {Object.values(LegalForm).map((value) => (
                      <SelectItem key={value} value={value}>
                        {
                          t(
                            `mandanten_create.step1.legal_forms_dropdown.${value.toLowerCase()}` as TranslationKey,
                          ) as string
                        }
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>

      {/* Address Section - Reference from CreateMandantModal.tsx */}
      <AddressList
        form={form}
        name="addresses"
        description={t("mandanten_create.step1.addresses_description")}
      />

      {/* Settings Section */}
      <div className="space-y-4 mt-8">
        <h3 className="font-medium text-sm">
          {t("mandanten_create.step1.settings")}
        </h3>
        <div className="border rounded-lg p-4 space-y-4 ">
          <FormField
            control={form.control}
            name="accountLength"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>
                    {t("mandanten_create.step1.account_length")}
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "mandanten_create.step1.account_length_placeholder",
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="4">
                        {t("mandanten_create.step1.account_length_4")}
                      </SelectItem>
                      <SelectItem value="5">
                        {t("mandanten_create.step1.account_length_5")}
                      </SelectItem>
                      <SelectItem value="6">
                        {t("mandanten_create.step1.account_length_6")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="skr"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>
                  {t("mandanten_create.step1.skr_selection")}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-row space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="03" id="skr03" />
                      <FormLabel htmlFor="skr03">
                        {t("mandanten_create.step1.skr03")}
                      </FormLabel>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="04" id="skr04" />
                      <FormLabel htmlFor="skr04">
                        {t("mandanten_create.step1.skr04")}
                      </FormLabel>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vorsteuerabzug"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    {t("mandanten_create.step1.input_tax_deduction")}
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
